import { useEffect, useState } from "react";
import { api, formatError, fmtINR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Copy, Trash2, TicketPercent } from "lucide-react";

export default function StoreCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: "", discount_type: "percent", discount_value: 20, max_uses: 100, min_order_amount: 0, active: true });

  const load = () => api.get("/store/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/store/coupons", { ...f, code: f.code.toUpperCase() }); toast.success("Coupon created"); setOpen(false); setF({ code: "", discount_type: "percent", discount_value: 20, max_uses: 100, min_order_amount: 0, active: true }); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const toggle = async (id) => { await api.patch(`/store/coupons/${id}/toggle`); load(); };
  const del = async (id) => { await api.delete(`/store/coupons/${id}`); toast.success("Deleted"); load(); };
  const copy = (code) => { navigator.clipboard.writeText(code); toast.success("Copied"); };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="store-coupons-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Coupons</h1><p className="text-slate-500">Promo codes customers can redeem at checkout</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-orange-500 hover:bg-orange-600" data-testid="btn-create-coupon"><Plus className="w-4 h-4 mr-2" />New Coupon</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Create coupon</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="LAUNCH50" data-testid="coupon-code" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Discount type</Label>
                  <Select value={f.discount_type} onValueChange={(v) => setF({ ...f, discount_type: v })}>
                    <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percent (%)</SelectItem>
                      <SelectItem value="flat">Flat (₹)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Value</Label><Input type="number" step="0.5" value={f.discount_value} onChange={(e) => setF({ ...f, discount_value: +e.target.value })} data-testid="coupon-value" /></div>
                <div><Label>Max uses</Label><Input type="number" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: +e.target.value })} /></div>
                <div><Label>Min order (₹)</Label><Input type="number" value={f.min_order_amount} onChange={(e) => setF({ ...f, min_order_amount: +e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} className="bg-orange-500 hover:bg-orange-600" data-testid="coupon-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Uses</TableHead><TableHead>Min order</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {coupons.map((c) => (
              <TableRow key={c.id} data-testid={`coupon-row-${c.code}`}>
                <TableCell><div className="font-mono font-semibold flex items-center gap-2"><TicketPercent className="w-4 h-4 text-orange-500" />{c.code}</div></TableCell>
                <TableCell className="font-mono">{c.discount_type === "percent" ? `${c.discount_value}%` : fmtINR(c.discount_value)}</TableCell>
                <TableCell className="font-mono">{c.used_count || 0} / {c.max_uses}</TableCell>
                <TableCell className="font-mono">{fmtINR(c.min_order_amount || 0)}</TableCell>
                <TableCell><Badge className={c.active ? "bg-emerald-500" : "bg-slate-400"}>{c.active ? "Active" : "Paused"}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => copy(c.code)}><Copy className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(c.id)} data-testid={`toggle-coupon-${c.code}`}>{c.active ? "Pause" : "Resume"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!coupons.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No coupons yet — create one to boost launch-day scans</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
